import React, { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  X, Plus, Trash2, Edit2, Check, Calendar,
  TrendingUp, TrendingDown
} from 'lucide-react';
import { goalsApi } from '../../services/api';
import { toast } from 'sonner';
import { useSafeMode } from '../../contexts/SafeModeContext';

// Confidence options for data points
const CONFIDENCE_OPTIONS = [
  { value: 'high', label: 'High Confidence', color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400' },
  { value: 'medium', label: 'Medium Confidence', color: 'bg-amber-500', textColor: 'text-amber-600 dark:text-amber-400' },
  { value: 'low', label: 'Low Confidence', color: 'bg-red-500', textColor: 'text-red-600 dark:text-red-400' },
];

/**
 * Format a value based on metric format
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    case 'time':
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const mins = Math.round(num % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${num}m`;
    default:
      return num.toLocaleString();
  }
};

/**
 * Get suffix for metric format input
 */
const getFormatSuffix = (format) => {
  switch (format) {
    case 'currency': return '$';
    case 'percent': return '%';
    case 'time': return 'min';
    default: return '';
  }
};

/**
 * Get today's date formatted
 */
const getTodayFormatted = () => {
  const today = new Date();
  return today.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const getTodayISO = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Data Log Modal - Add, Edit, Delete data points for a KPI
 * Jumbo modal for comfortable data entry
 */
export const DataLogModal = ({
  isOpen,
  onClose,
  goalId,
  kpi,
  onDataChange
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [newValue, setNewValue] = useState('');
  const [newConfidence, setNewConfidence] = useState('medium');
  const [newNote, setNewNote] = useState('');
  const [editingDate, setEditingDate] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editConfidence, setEditConfidence] = useState('medium');
  const [editNote, setEditNote] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { kpi_id, metric_name, metric_format, direction, data_points = [], target_value, start_value } = kpi;
  const DirectionIcon = direction === 'lower_is_better' ? TrendingDown : TrendingUp;
  const todayISO = getTodayISO();
  
  // Sort data points by date descending (most recent first)
  const sortedPoints = useMemo(() => {
    return [...data_points].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data_points]);
  
  // Check if today already has an entry
  const todayHasEntry = sortedPoints.some(p => p.date === todayISO);
  
  const handleAddPoint = async () => {
    if (!newValue) {
      toast.error('Please enter a value');
      return;
    }
    
    // Check if date already exists
    if (todayHasEntry) {
      toast.error('You have already logged a value for today. Edit or delete the existing entry.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await goalsApi.logDataPoint(goalId, kpi_id, {
        date: todayISO,
        value: parseFloat(newValue),
        confidence: newConfidence,
        note: newNote || null
      });
      
      if (response.data?.success) {
        toast.success(`Logged ${formatValue(parseFloat(newValue), metric_format)} for today`);
        onDataChange?.();
        setNewValue('');
        setNewConfidence('medium');
        setNewNote('');
      }
    } catch (err) {
      console.error('Failed to log data point:', err);
      toast.error('Failed to log data point');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdatePoint = async (originalDate) => {
    if (!editValue) {
      toast.error('Please enter a value');
      return;
    }
    
    setLoading(true);
    try {
      const response = await goalsApi.logDataPoint(goalId, kpi_id, {
        date: originalDate,
        value: parseFloat(editValue),
        confidence: editConfidence,
        note: editNote || null
      });
      
      if (response.data?.success) {
        toast.success('Data point updated');
        onDataChange?.();
        setEditingDate(null);
      }
    } catch (err) {
      console.error('Failed to update data point:', err);
      toast.error('Failed to update data point');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeletePoint = async (date) => {
    setLoading(true);
    try {
      const response = await goalsApi.deleteDataPoint(goalId, kpi_id, date);
      
      if (response.data?.success) {
        toast.success('Data point deleted');
        onDataChange?.();
      }
    } catch (err) {
      console.error('Failed to delete data point:', err);
      toast.error('Failed to delete data point');
    } finally {
      setLoading(false);
    }
  };
  
  const startEditing = (point) => {
    setEditingDate(point.date);
    setEditValue(point.value.toString());
    setEditConfidence(point.confidence || 'medium');
    setEditNote(point.note || '');
  };
  
  const cancelEditing = () => {
    setEditingDate(null);
    setEditValue('');
    setEditConfidence('medium');
    setEditNote('');
  };
  
  // Get confidence color for display
  const getConfidenceColor = (confidence) => {
    const option = CONFIDENCE_OPTIONS.find(c => c.value === confidence);
    return option?.color || 'bg-gray-400';
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal - 25% wider */}
      <div className={cn(
        "relative z-10 w-full max-w-xl rounded-xl shadow-xl",
        colors.bgCard
      )}>
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between px-5 py-4 border-b",
          colors.borderPrimary
        )}>
          <div>
            <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
              Update KPI Progress
            </h2>
            <p className={cn("text-sm flex items-center gap-1.5 mt-0.5", colors.textSecondary)}>
              {metric_name}
              <DirectionIcon className={cn(
                "w-4 h-4",
                direction === 'lower_is_better' ? "text-blue-500" : "text-green-500"
              )} />
            </p>
          </div>
          <button 
            onClick={onClose}
            className={cn("p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800", colors.textMuted)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Add New Entry Form */}
        <div className={cn("px-5 py-4 border-b", colors.borderPrimary, "bg-indigo-50/50 dark:bg-indigo-900/20")}>
          {/* Date - Read only */}
          <div className="mb-4">
            <Label className={cn("text-sm font-medium mb-1.5 block", colors.textPrimary)}>Date</Label>
            <div className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-sm",
              colors.bgTertiary
            )}>
              <Calendar className={cn("w-4 h-4", colors.textMuted)} />
              <span className={cn("font-medium", colors.textPrimary)}>
                {getTodayFormatted()}
              </span>
            </div>
          </div>
          
          {/* Value and Confidence - Side by side */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className={cn("text-sm font-medium mb-1.5 block", colors.textPrimary)}>
                Current Value
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Enter value"
                  disabled={todayHasEntry}
                  className={cn(
                    "pr-10",
                    colors.bgInput, 
                    colors.borderPrimary,
                    todayHasEntry && "opacity-50 cursor-not-allowed"
                  )}
                  data-testid="new-data-value"
                />
                {getFormatSuffix(metric_format) && (
                  <span className={cn(
                    "absolute right-3 top-1/2 -translate-y-1/2 text-sm",
                    colors.textMuted
                  )}>
                    {getFormatSuffix(metric_format)}
                  </span>
                )}
              </div>
            </div>
            <div>
              <Label className={cn("text-sm font-medium mb-1.5 block", colors.textPrimary)}>
                On Track?
              </Label>
              <Select 
                value={newConfidence} 
                onValueChange={setNewConfidence}
                disabled={todayHasEntry}
              >
                <SelectTrigger className={cn(
                  colors.bgInput, 
                  colors.borderPrimary,
                  todayHasEntry && "opacity-50 cursor-not-allowed"
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {CONFIDENCE_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <div className="flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", c.color)} />
                        <span className={c.textColor}>{c.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Reference values */}
          <div className={cn("flex items-center gap-4 mb-4 text-xs", colors.textMuted)}>
            <span>Start: <strong className={colors.textPrimary}>{formatValue(start_value, metric_format)}</strong></span>
            <span>Target: <strong className="text-green-600">{formatValue(target_value, metric_format)}</strong></span>
          </div>
          {todayHasEntry && (
            <p className="text-amber-600 text-xs mb-4">
              You already logged a value for today. Edit it below or delete to log a new one.
            </p>
          )}
          
          {/* Notes */}
          <div className="mb-4">
            <Label className={cn("text-sm font-medium mb-1.5 block", colors.textPrimary)}>
              Notes <span className={colors.textMuted}>(optional)</span>
            </Label>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add context, observations, or reasons for this value..."
              disabled={todayHasEntry}
              rows={2}
              className={cn(
                "resize-none text-sm",
                colors.bgInput, 
                colors.borderPrimary,
                todayHasEntry && "opacity-50 cursor-not-allowed"
              )}
              data-testid="new-data-note"
            />
          </div>
          
          {/* Add Button */}
          <Button
            onClick={handleAddPoint}
            disabled={loading || !newValue || todayHasEntry}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="add-data-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Log Progress
          </Button>
        </div>
        
        {/* Historical Data Points */}
        <div className="px-5 py-4">
          <h3 className={cn("text-xs font-semibold uppercase tracking-wider mb-3", colors.textMuted)}>
            History ({sortedPoints.length} entries)
          </h3>
          
          <div className="max-h-[220px] overflow-y-auto -mx-1 px-1">
            {sortedPoints.length === 0 ? (
              <div className={cn("p-6 text-center rounded-lg border border-dashed", colors.borderPrimary)}>
                <Calendar className={cn("w-8 h-8 mx-auto mb-2 opacity-30", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>No data points yet</p>
                <p className={cn("text-xs mt-0.5", colors.textMuted)}>Add your first entry above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedPoints.map((point) => (
                  <div 
                    key={point.date}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border",
                      editingDate === point.date 
                        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700" 
                        : cn(colors.bgTertiary, colors.borderPrimary)
                    )}
                  >
                    {editingDate === point.date ? (
                      // Edit mode
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className={cn("w-3.5 h-3.5", colors.textMuted)} />
                          <span className={cn("text-xs font-medium", colors.textPrimary)}>{point.date}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className={cn("h-8 text-sm", colors.bgInput, colors.borderPrimary)}
                            placeholder="Value"
                          />
                          <Select value={editConfidence} onValueChange={setEditConfidence}>
                            <SelectTrigger className={cn("h-8 text-sm", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              {CONFIDENCE_OPTIONS.map(c => (
                                <SelectItem key={c.value} value={c.value}>
                                  <div className="flex items-center gap-2">
                                    <span className={cn("w-2 h-2 rounded-full", c.color)} />
                                    <span className={c.textColor}>{c.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          placeholder="Notes"
                          rows={2}
                          className={cn("resize-none text-sm", colors.bgInput, colors.borderPrimary)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdatePoint(point.date)}
                            disabled={loading}
                            className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                            className="h-7 text-xs"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {/* Confidence indicator dot */}
                            <span className={cn("w-2 h-2 rounded-full", getConfidenceColor(point.confidence))} />
                            <Calendar className={cn("w-3 h-3", colors.textMuted)} />
                            <span className={cn("text-xs", colors.textMuted)}>{point.date}</span>
                            {point.date === todayISO && (
                              <span className="text-xs bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded-full">
                                Today
                              </span>
                            )}
                          </div>
                          <div className={cn("text-base font-semibold", colors.textPrimary)}>
                            {formatValue(point.value, metric_format)}
                          </div>
                          {point.note && (
                            <p className={cn("text-xs mt-0.5 truncate", colors.textSecondary)} title={point.note}>
                              {point.note}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditing(point)}
                            className="h-7 w-7 p-0 opacity-60 hover:opacity-100"
                            data-testid={`edit-point-${point.date}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletePoint(point.date)}
                            disabled={loading}
                            className="h-7 w-7 p-0 opacity-60 hover:opacity-100 text-red-500 hover:text-red-600"
                            data-testid={`delete-point-${point.date}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className={cn(
          "flex items-center justify-end px-5 py-3 border-t",
          colors.borderPrimary
        )}>
          <Button variant="outline" onClick={onClose} size="sm">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataLogModal;
